import { useEffect, useRef } from 'react';

export interface UseIdleCallbackOptions {
  /** When true, do not schedule the callback. */
  skip?: boolean;

  /** Max wait time (ms) before running without waiting for idle. */
  timeout?: number;
}

// Polyfill for browsers without requestIdleCallback support (e.g., Safari).
if (typeof window !== 'undefined' && !window.requestIdleCallback) {
  window.requestIdleCallback = function requestIdleCallbackPolyfill(
    callback: IdleRequestCallback,
    options?: IdleRequestOptions,
  ): number {
    const start = Date.now();
    return setTimeout(() => {
      callback({
        didTimeout: false,
        timeRemaining: () => Math.max(0, 50 - (Date.now() - start)),
      });
    }, options?.timeout ?? 1) as unknown as number;
  };

  window.cancelIdleCallback = function cancelIdleCallbackPolyfill(id: number): void {
    clearTimeout(id);
  };
}

/**
 * Schedule a callback to run when the browser is idle.
 */
export default function useIdleCallback(callback: () => void, options?: UseIdleCallbackOptions): void {
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (options?.skip) {
      return undefined;
    }

    const id = requestIdleCallback(
      () => {
        callbackRef.current();
      },
      options?.timeout ? { timeout: options.timeout } : undefined,
    );

    return () => cancelIdleCallback(id);
  }, [options?.timeout, options?.skip]);
}
